"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useRef } from "react";
import { Layers } from "lucide-react";
import type { Dictionary } from "@/dictionaries/types";
import type { CollectionsResponse } from "@/features/collections/types";
import { useCollections } from "@/features/collections/hooks/useCollections";
import { RouteHero } from "@/features/home-v2/RouteHero";
import CollectionsResults from "@/features/collections/listing/CollectionsResults";
import CollectionsToolbar from "@/features/collections/listing/CollectionsToolbar";
import { useCollectionsListing } from "@/features/collections/listing/useCollectionsListing";
import "@/features/collections/listing/listing.css";
import { useGridColumns } from "@/hooks/useGridColumns";
import { pageSizeFor } from "@/lib/grid-rows";
type Props = {
  lang: "fa" | "en";
  dict: Dictionary;
  initialData: CollectionsResponse;
  /** The next page from the server, for the cards the Top tab's grid runs on into. */
  initialNext?: CollectionsResponse;
  perPage: number;
  /** The URL set `per_page`: honoured as given, never re-cut to the grid. */
  perPageFixed?: boolean;
  title: string;
};

/**
 * `/collection` — the list, in v2. This file only composes: the route hero
 * (the same component and the same copy length as `/market`, so the two
 * heroes are one height), the quick bar, and the results. The behaviour —
 * URL state, tabs, dual view, category source, SSR seed — lives unchanged in
 * `useCollectionsListing` and `useCollectionResults`. The detail page at
 * `/collection/[contract]` is untouched.
 */
export default function CollectionsPageClient(t0) {
  const $ = _c(70);
  const {
    lang,
    dict,
    initialData,
    initialNext,
    perPage: basePerPage,
    perPageFixed: t1
  } = t0;
  const perPageFixed = t1 === undefined ? false : t1;
  const isFa = lang === "fa";
  let t2;
  if ($[0] !== isFa) {
    t2 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const tt = t2;
  const t3 = isFa ? "fa-IR" : "en-US";
  let t4;
  if ($[2] !== t3) {
    t4 = new Intl.NumberFormat(t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const number = t4;
  const listing = useCollectionsListing(isFa);
  const grid = useGridColumns();
  let t5;
  if ($[4] !== basePerPage || $[5] !== grid || $[6] !== listing.view || $[7] !== perPageFixed) {
    t5 = perPageFixed || listing.view !== "grid" ? basePerPage : pageSizeFor(grid.columns, basePerPage);
    $[4] = basePerPage;
    $[5] = grid;
    $[6] = listing.view;
    $[7] = perPageFixed;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const perPage = t5;
  let t6;
  if ($[9] !== lang || $[10] !== listing.filters || $[11] !== listing.page || $[12] !== perPage) {
    t6 = {
      page: listing.page,
      perPage,
      lang,
      ...listing.filters
    };
    $[9] = lang;
    $[10] = listing.filters;
    $[11] = listing.page;
    $[12] = perPage;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = listing.categoryEndpoint ? undefined : initialData;
  const t8 = !listing.categoryEndpoint;
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = {
      initialData: t7,
      enabled: t8
    };
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const countQuery = useCollections(t6, t9);
  const total = listing.categoryEndpoint ? null : countQuery.data?.data?.page?.total ?? null;
  const barRef = useRef(null);
  const resultsRef = useRef(null);
  let t10;
  if ($[17] !== listing) {
    t10 = target => {
      listing.setPage(target);
      const results = resultsRef.current;
      if (!results) {
        return;
      }
      const bar = barRef.current;
      const barBottom = bar ? parseFloat(getComputedStyle(bar).top) + bar.offsetHeight : 0;
      const top = results.getBoundingClientRect().top + window.scrollY - barBottom - 12;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: reduce ? "auto" : "smooth"
      });
    };
    $[17] = listing;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const goToPage = t10;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Layers aria-hidden={true} />;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== tt) {
    t12 = tt("\u06A9\u0627\u0644\u06A9\u0634\u0646\u200C\u0647\u0627\u06CC NFTBaz", "NFTBaz collections");
    $[20] = tt;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== tt) {
    t14 = tt("\u06A9\u0627\u0644\u06A9\u0634\u0646\u200C\u0647\u0627", "Collections");
    $[24] = tt;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== tt) {
    t15 = tt("\u06A9\u0627\u0644\u06A9\u0634\u0646\u200C\u0647\u0627\u06CC \u0647\u0646\u0631\u0645\u0646\u062F\u0627\u0646 \u0627\u06CC\u0631\u0627\u0646\u06CC \u0648 \u067E\u0631\u0648\u0698\u0647\u200C\u0647\u0627\u06CC \u062C\u0647\u0627\u0646\u06CC \u0631\u0627 \u06A9\u0634\u0641\u060C \u0645\u0642\u0627\u06CC\u0633\u0647 \u0648 \u062F\u0646\u0628\u0627\u0644 \u06A9\u0646\u06CC\u062F.", "Discover, compare and follow collections by Iranian artists and global projects.");
    $[26] = tt;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== tt) {
    t16 = tt("\u0646\u0645\u0627\u06CC \u06A9\u0644\u06CC \u06A9\u0627\u0644\u06A9\u0634\u0646\u200C\u0647\u0627", "Collections overview");
    $[28] = tt;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== tt) {
    t17 = tt("\u06A9\u0627\u0644\u06A9\u0634\u0646 \u062F\u0631 \u0641\u0647\u0631\u0633\u062A", "Collections listed");
    $[30] = tt;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t17) {
    t18 = <span>{t17}</span>;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== number || $[35] !== total) {
    t19 = total == null ? "\u2014" : number.format(total);
    $[34] = number;
    $[35] = total;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t19) {
    t20 = <strong aria-live="polite">{t19}</strong>;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== tt) {
    t21 = tt("\u0641\u06CC\u0644\u062A\u0631\u0647\u0627 \u0648 \u0645\u0631\u062A\u0628\u200C\u0633\u0627\u0632\u06CC \u062F\u0631 \u0646\u0634\u0627\u0646\u06CC \u0635\u0641\u062D\u0647 \u0630\u062E\u06CC\u0631\u0647 \u0645\u06CC\u200C\u0634\u0648\u0646\u062F", "Filters and sorting are kept in the page URL");
    $[39] = tt;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t21) {
    t22 = <small>{t21}</small>;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== t16 || $[44] !== t18 || $[45] !== t20 || $[46] !== t22) {
    t23 = <div className="v2-route-hero-aside v2-route-hero-ledger" aria-label={t16}>{t18}{t20}{t22}</div>;
    $[43] = t16;
    $[44] = t18;
    $[45] = t20;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t13 || $[49] !== t14 || $[50] !== t15 || $[51] !== t23) {
    t24 = <RouteHero eyebrow={t13} title={t14} description={t15} aside={t23} />;
    $[48] = t13;
    $[49] = t14;
    $[50] = t15;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== dict || $[54] !== lang || $[55] !== listing) {
    t25 = <CollectionsToolbar ref={barRef} listing={listing} dict={dict} lang={lang} />;
    $[53] = dict;
    $[54] = lang;
    $[55] = listing;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== dict || $[58] !== goToPage || $[59] !== grid || $[60] !== initialData || $[61] !== initialNext || $[62] !== lang || $[63] !== listing || $[64] !== perPage) {
    t26 = <div ref={resultsRef}><CollectionsResults listing={listing} lang={lang} dict={dict} initialData={initialData} initialNext={initialNext} pageSize={perPage} onPage={goToPage} grid={grid} /></div>;
    $[57] = dict;
    $[58] = goToPage;
    $[59] = grid;
    $[60] = initialData;
    $[61] = initialNext;
    $[62] = lang;
    $[63] = listing;
    $[64] = perPage;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== t24 || $[67] !== t25 || $[68] !== t26) {
    t27 = <main className="v2-page v2-clx" id="v2-main">{t24}{t25}{t26}</main>;
    $[66] = t24;
    $[67] = t25;
    $[68] = t26;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  return t27;
}